import React, { FC, useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ModalTheme } from 'modules/core/enums';
import { clearLoanVariablesCurrentVehicle } from 'store/reducers/RACAffordabilityEngine.reducer';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, store } from 'store/store';
import { ModalDialog } from 'components/ModalDialog';
import { Modal } from 'react-bootstrap';
import { getActiveModalsState, getVendorName } from 'store/selectors/ui.selector';
import { getUser } from 'store/selectors/auth.selector';
import { vehicleApi } from 'api';
import {
  getAddUserVehicleFlags,
  getCreatedUserVehicle,
  getUpdateUserVehicleFlags,
} from 'store/selectors/vehicle.selector';
import { resetCreatedUserVehicle } from 'store/reducers/vehicle.reducer';
import {
  resetComponentState,
  setLoanTermsList,
  setLoanVariables,
  setCurrentModalId,
} from 'store/reducers/loanVariablesDialog.reducer';
import { startSessionReplay } from 'utils/datadog';
import { getLoanVariablesDialogState } from 'store/selectors/loanVariablesDialog.selector';
import { IDealSourceData, IDealSourceDataFlags } from 'modules/core/types/dealSource';
import { IRACAffordabilityEngineForm } from 'modules/RACAffordabilityEngine/types/RACAffordabilityEngineForm';
import { ADJUST_DEAL_DIALOG_STYLES, EMPTY_STRING, MODAL_DIALOG_ID, MODAL_DIALOG_WIDTH } from 'modules/core/constants';
import { useArraFinanceBackAdds } from 'modules/core/hooks/useArraFinanceBackAdds';
import { useIsAllyLender } from 'modules/core/hooks/useIsAllyLender';
import { useIsMtbLender } from 'modules/core/hooks/useIsMtbLender';

import { LoanVariablesFormContent } from './components/LoanVariablesFormContent';
import { Loader } from './Loader';
import { FormContentVisibility, HeaderContainer, HeaderLogo } from './styles';
import { getLoanVariablesCurrentVehicle } from '../../../store/selectors/RACAffordabilityEngine.selector';
import { LoanVariablesFailedBlock } from './components/LoanVariablesFailedBlock/LoanVariablesFailedBlock';
import { DEFAULT_LOAN_VARIABLES_VALUES } from './consts/consts';
import { DEFAULT_NET_TRADE_IN_VALUES } from './modals/Calculators/NetTradeInCalculator/consts/consts';
import { DEFAULT_SALES_FEES_VALUES } from './modals/Calculators/SaleFeesCalculator/consts/consts';
import { isDealSourceVehicleInfoMissing } from './utils/dealSourceVehicle.utils';

export interface ILoanVariablesDialog {
  modalId: string;
  dealSourceData: IDealSourceData;
  dealSourceDataFlags: IDealSourceDataFlags;
  resetDealSourceDataFlags: Function;
  headerLogoSrc: string;
  isLender?: boolean;
  isRac?: boolean;
  taxExemptFields?: string[];
  isLoanVariablesDialogActive: boolean;
  isDealSourceCar?: boolean;
  manuallyCreated?: boolean;
  isAddedUserVehicle?: boolean;
  resetState: () => void;
  onHide: () => void;
  isMakeItWork: boolean;
  isSubmitR1DealBlockedByControls?: boolean;
  getLoanVariablesData: any;
  formHistory: any;
  setFormHistory: any;
  loanTermValue: any;
  defaultLoanTermValue?: any;
  routeOneUserId?: string | null;
  ltvCategories: any;
  apr: any;
  guidebookValue: number;
  getTargetPayment: Function;
  getTotalAmountFinanced: Function;
  getCurrentPayment: Function;
  sortedVehiclesSelector: any;
  vehiclesFetchingFlagsSelector: any;
  isBalloonPaymentAvailable?: boolean;
  isEligibleForAfg?: boolean;
  defaultBalloonToggle?: boolean;
  isReadyToFetchLoanData?: boolean;
  isSalespersonLimitedView?: boolean;
  shouldRenderRouteOneUserId?: boolean;
  getActualPaymentInfoMessage?: (loanVariables: any, availableDealStructureStatus?: any) => string;
  getLtvCategories?: (loanVariables: any, availableDealStructureStatus: any) => any[];
  transformedLoanVariables?: any;
  isShopByCallbackMode?: boolean;
  isRouteOneFlow?: boolean;
  isRouteOneFlowForSbbOrSbc?: boolean;
  isDealerSsoSbbOrSbcFlow?: boolean;
  isDisplayPolicyStatusMessage?: boolean;
  onOdometerChange?: (odometer: number | null | undefined) => void;
}

export const LoanVariablesDialog: FC<ILoanVariablesDialog> = ({
  modalId,
  dealSourceData,
  dealSourceDataFlags,
  resetDealSourceDataFlags,
  headerLogoSrc,
  isLender = false,
  isRac = false,
  taxExemptFields,
  isLoanVariablesDialogActive,
  isDealSourceCar = false,
  manuallyCreated = false,
  isAddedUserVehicle = false,
  resetState,
  onHide,
  isMakeItWork,
  isSubmitR1DealBlockedByControls = false,
  getLoanVariablesData,
  formHistory,
  setFormHistory,
  loanTermValue,
  defaultLoanTermValue,
  routeOneUserId,
  ltvCategories,
  apr,
  guidebookValue,
  getTargetPayment,
  getTotalAmountFinanced,
  getCurrentPayment,
  sortedVehiclesSelector,
  vehiclesFetchingFlagsSelector,
  isBalloonPaymentAvailable = false,
  isEligibleForAfg = false,
  defaultBalloonToggle = false,
  isReadyToFetchLoanData = true,
  isSalespersonLimitedView = false,
  getActualPaymentInfoMessage,
  getLtvCategories,
  transformedLoanVariables,
  shouldRenderRouteOneUserId = false,
  isShopByCallbackMode = false,
  isRouteOneFlow = false,
  isRouteOneFlowForSbbOrSbc = false,
  isDealerSsoSbbOrSbcFlow = false,
  isDisplayPolicyStatusMessage = true,
  onOdometerChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const { isLoading, isFetchingFailed, loanVariables, loanTermsList } = useSelector(getLoanVariablesDialogState);
  const createdUserVehicle = useSelector(getCreatedUserVehicle);
  const vendorName = useSelector(getVendorName);
  const addUserVehicleFlags = useSelector(getAddUserVehicleFlags);
  const updateUserVehicleFlags = useSelector(getUpdateUserVehicleFlags);
  const currentVehicleData = useSelector(getLoanVariablesCurrentVehicle);
  const user = useSelector(getUser);
  const { dealSourceBackAdds } = useArraFinanceBackAdds();
  const isAllyLender = useIsAllyLender(isLender);
  const isMtbLender = useIsMtbLender(isLender);

  const { getValues: commonGetValues } = useFormContext<IRACAffordabilityEngineForm>();

  const {
    totalFrontAndBackEndsAddsDialog,
    totalNetDownPaymentDialog,
    grossMonthlyIncome,
    lendersMonthlyIncomeModal,
    feesAndTaxes,
    cashDownOverridden,
    estimatedFeesOverridden,
    tier,
    applyCpo,
  } = commonGetValues() as any;

  const { tier: tierName } = tier;

  const totalNetTradeIn = totalNetDownPaymentDialog.tradeValue - totalNetDownPaymentDialog.amountOwed;
  const netTradeInFormDefaultValue = {
    ...DEFAULT_NET_TRADE_IN_VALUES.netTradeInForm,
    netTradeInTotal: totalNetTradeIn,
    tradeInValue: totalNetDownPaymentDialog.tradeValue,
    amountOwnedOnTrade: totalNetDownPaymentDialog.amountOwed,
    lienHolder: totalNetDownPaymentDialog.lienHolder,
    year: totalNetDownPaymentDialog.year,
    make: totalNetDownPaymentDialog.make,
    model: totalNetDownPaymentDialog.model,
    otherYear: totalNetDownPaymentDialog.otherYear,
    otherMake: totalNetDownPaymentDialog.otherMake,
    otherModel: totalNetDownPaymentDialog.otherModel,
  };
  const isCashDownOverridden = Boolean(cashDownOverridden);
  const isEstimatedFeesOverridden = Boolean(estimatedFeesOverridden);
  const toSubstituteFormValuesWithDealSourceData = isMakeItWork && Object.keys(dealSourceData || {}).length > 0;

  const defaultSalesFeesValue = toSubstituteFormValuesWithDealSourceData
    ? isEstimatedFeesOverridden
      ? feesAndTaxes.estimatedFees || 0
      : (dealSourceData.taxes_fees?.sales_fees || 0) + (dealSourceData.taxes_fees?.title_reg_license || 0)
    : feesAndTaxes.estimatedFees || 0;

  const isDealSourceVehicle =
    ((loanVariables?.id || currentVehicleData?.id) &&
      (loanVariables?.id || currentVehicleData?.id) === dealSourceData?.vehicle_id) ||
    isDealSourceCar;

  const isRequiredVehicleInfoPending = isDealSourceVehicleInfoMissing(
    isDealSourceVehicle,
    loanVariables?.vin || currentVehicleData?.vin,
    dealSourceData,
  );

  const [isInitialBalloonSetupComplete, setIsInitialBalloonSetupComplete] = useState(false);

  /*
   * With balloon payment on by default the loan data alone is not enough to render the modal: the
   * balloon endpoints still have to answer and the calculation has to be redone with their values.
   * Keep the initial loader up for that whole sequence instead of letting each step show its own.
   */
  const isAwaitingInitialBalloonSetup = Boolean(
    isLender &&
      isBalloonPaymentAvailable &&
      isEligibleForAfg &&
      defaultBalloonToggle &&
      loanVariables?.vin &&
      !isFetchingFailed &&
      !isInitialBalloonSetupComplete,
  );

  const handleInitialBalloonSetupComplete = useCallback(() => setIsInitialBalloonSetupComplete(true), []);

  useEffect(() => {
    dispatch(setCurrentModalId(modalId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalId]);

  const handleHide = useCallback(() => {
    vehicleApi.cancelGetLenderLoanVariablesInfo();
    vehicleApi.cancelGetLoanVariablesInfo();
    dispatch(clearLoanVariablesCurrentVehicle());
    resetState();
    dispatch(resetComponentState());
    onHide();
  }, [dispatch, resetState, onHide]);

  const handleHideWithResetVehicle = useCallback(() => {
    handleHide();
    dispatch(resetCreatedUserVehicle());
  }, [dispatch, handleHide]);

  /*
   * Handles the case when the user navigates to another page without closing the modal.
   * Checking activeModals prevents handleHide from being called twice
   * when the user closes the modal by clicking the button.
   */
  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      const activeModals = getActiveModalsState(store.getState());
      const isLoanDialogActive = activeModals.includes(MODAL_DIALOG_ID.LOAN_VARIABLES);

      if (isLoanDialogActive) handleHide();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackJDPUsage = useCallback(async () => {
    if (user?.is_sso && !user?.isWithInventory) return;

    await vehicleApi.postJDPUsage(currentVehicleData?.id || dealSourceData?.vehicle_id, vendorName);
  }, [currentVehicleData?.id, dealSourceData?.vehicle_id, user?.isWithInventory, user?.is_sso, vendorName]);

  const headerTitle = useCallback(
    () => <HeaderContainer>{headerLogoSrc && <HeaderLogo src={headerLogoSrc} />}</HeaderContainer>,
    [headerLogoSrc],
  );

  useEffect(() => {
    if (
      isReadyToFetchLoanData &&
      (!isDealSourceCar || dealSourceDataFlags.isReceived) &&
      !addUserVehicleFlags.isLoaded &&
      (!updateUserVehicleFlags.isLoaded || dealSourceDataFlags.isReceived) &&
      (!isAddedUserVehicle ? isDealSourceCar || currentVehicleData?.id : createdUserVehicle.id) &&
      Object.keys(loanVariables || {}).length === 0
    ) {
      getLoanVariablesData(defaultValues);
      dispatch(resetDealSourceDataFlags());
      trackJDPUsage();

      if (isMakeItWork) {
        startSessionReplay(user);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    getLoanVariablesData,
    isLoanVariablesDialogActive,
    trackJDPUsage,
    dealSourceDataFlags.isReceived,
    isReadyToFetchLoanData,
  ]);

  const defaultValues = {
    loanVariablesForm: {
      ...DEFAULT_LOAN_VARIABLES_VALUES.loanVariablesForm,
      vin: loanVariables?.vin,
      stock: currentVehicleData?.stock,
      tier: tierName,
      retailPrice: currentVehicleData?.current_price || 0,
      netTradeInForm: netTradeInFormDefaultValue,
      mileage: currentVehicleData?.odometer,
      guidebookValue: guidebookValue,
      monthlyIncome: grossMonthlyIncome,
      addOns: totalFrontAndBackEndsAddsDialog.addOns || 0,
      vsc: totalFrontAndBackEndsAddsDialog.vsc || 0,
      gap: isAllyLender || isMtbLender ? 0 : totalFrontAndBackEndsAddsDialog.gap || 0,
      cashDown: totalNetDownPaymentDialog.cashDown || 0,
      participation: 0,
      balloonToggle: defaultBalloonToggle,
      balloonPayment: 0,
      afgTrim: EMPTY_STRING,
      annualMileage: EMPTY_STRING,
      engine: [],
      lenderList: [],
      monthlyIncomeForm: lendersMonthlyIncomeModal,
      netTradeIn: totalNetTradeIn,
      payment: getTargetPayment(),
      totalSalesFees: feesAndTaxes.estimatedFees,
      salesFeesForm: {
        ...DEFAULT_SALES_FEES_VALUES.salesFeesForm,
        salesDocumentFees: feesAndTaxes.estimatedFees,
        salesFees: feesAndTaxes.estimatedFees,
      },
      currentPayment: getCurrentPayment(),
      apr: apr,
      loanTerm: { title: defaultLoanTermValue || loanTermsList[0], value: defaultLoanTermValue || loanTermsList[0] },
      loanTermsList: loanTermsList,
      totalAmountFinanced: getTotalAmountFinanced(),
      lineThree: loanVariables?.total_financed_exc_back_end ?? getTotalAmountFinanced(),
      taxes: loanVariables?.taxes || 0,
      ltvCategories: ltvCategories,
      isVerifyDealButtonDisabled: false,
      routeOneUserId: routeOneUserId,
      vehicle_id: currentVehicleData?.id,
      applyCpo: Boolean(applyCpo),
      ...(isDealSourceVehicle && {
        taxes: dealSourceData.taxes_fees?.taxes,
        vehicle_id: dealSourceData.vehicle_id,
        retailPrice: dealSourceData.sale_vehicle.retail_price,
        mileage: dealSourceData.sale_vehicle.mileage,
        guidebookValue: dealSourceData?.sale_vehicle?.book_value ?? loanVariables?.guidebook_value ?? guidebookValue,
        vsc: dealSourceBackAdds ?? totalFrontAndBackEndsAddsDialog.vsc ?? 0,
      }),
      ...(isAddedUserVehicle && {
        vehicle_id: createdUserVehicle.id,
        retailPrice: createdUserVehicle.current_price,
        mileage: createdUserVehicle.odometer,
        guidebookValue: createdUserVehicle.book_value,
      }),
      ...(toSubstituteFormValuesWithDealSourceData && {
        cashDown: isCashDownOverridden
          ? totalNetDownPaymentDialog.cashDown || 0
          : dealSourceData.finance_details?.cash_down || 0,
        rebates: isCashDownOverridden ? 0 : dealSourceData.finance_details?.rebates || 0,
        salesFeesForm: {
          salesDocumentFees: isEstimatedFeesOverridden
            ? feesAndTaxes.estimatedFees || 0
            : dealSourceData.taxes_fees?.sales_fees || 0,
          salesFees: defaultSalesFeesValue,
          titleRegistrationLicense: isEstimatedFeesOverridden ? 0 : dealSourceData.taxes_fees?.title_reg_license || 0,
        },
        totalSalesFees: defaultSalesFeesValue,
        loanTerm: {
          title: loanTermValue || loanTermsList[0],
          value: loanTermValue || loanTermsList[0],
        },
      }),
    },
  };

  return (
    <ModalDialog
      show={isLoanVariablesDialogActive}
      onHide={handleHideWithResetVehicle}
      backdrop={isRequiredVehicleInfoPending ? 'static' : true}
      keyboard={!isRequiredVehicleInfoPending}
      modalTheme={ModalTheme.ADJUST_DEAL}
      minModalSize={MODAL_DIALOG_WIDTH.CUSTOM.PX_965}
      modalSize={MODAL_DIALOG_WIDTH.CUSTOM.PX_965}
      dialogClassName="adjust-deal-modal"
      backdropClassName="adjust-deal-backdrop"
      modalStyle={ADJUST_DEAL_DIALOG_STYLES}
      isAdjustDeal={true}
      showFooter={false}
      showHeader={false}
      isDirty={formHistory.length > 1}
    >
      {(isLoading || isAwaitingInitialBalloonSetup) && (
        <Modal.Body>
          <Loader />
        </Modal.Body>
      )}
      {!isLoading && isFetchingFailed && (
        <LoanVariablesFailedBlock handleHideWithResetVehicle={handleHideWithResetVehicle} headerTitle={headerTitle} />
      )}
      {!isLoading && !isFetchingFailed && (
        <FormContentVisibility $isHidden={isAwaitingInitialBalloonSetup}>
          <LoanVariablesFormContent
            modalId={modalId}
            isLender={isLender}
            isRac={isRac}
            taxExemptFields={taxExemptFields}
            isLoanVariablesDialogActive={isLoanVariablesDialogActive}
            currentVehicleData={currentVehicleData}
            defaultValues={defaultValues}
            loanVariables={transformedLoanVariables || loanVariables}
            loanTermsList={loanTermsList}
            isDealSourceVehicle={isDealSourceVehicle}
            manuallyCreated={manuallyCreated}
            formHistory={formHistory}
            handleHide={handleHide}
            handleHideWithResetVehicle={handleHideWithResetVehicle}
            setFormHistory={setFormHistory}
            setLoanVariables={setLoanVariables}
            setLoanTermsList={setLoanTermsList}
            isAddedUserVehicle={isAddedUserVehicle}
            createdUserVehicle={createdUserVehicle}
            dealSourceData={dealSourceData}
            dealSourceDataFlags={dealSourceDataFlags}
            resetDealSourceDataFlags={resetDealSourceDataFlags}
            modalTitle={headerTitle()}
            isSubmitR1DealBlockedByControls={isSubmitR1DealBlockedByControls}
            isBalloonPaymentAvailable={isBalloonPaymentAvailable}
            isEligibleForAfg={isEligibleForAfg}
            sortedVehiclesSelector={sortedVehiclesSelector}
            vehiclesFetchingFlagsSelector={vehiclesFetchingFlagsSelector}
            isSalespersonLimitedView={isSalespersonLimitedView}
            isMakeItWork={isMakeItWork}
            shouldRenderRouteOneUserId={shouldRenderRouteOneUserId}
            getActualPaymentInfoMessage={getActualPaymentInfoMessage}
            getLtvCategories={getLtvCategories}
            isShopByCallbackMode={isShopByCallbackMode}
            isRouteOneFlow={isRouteOneFlow}
            isRouteOneFlowForSbbOrSbc={isRouteOneFlowForSbbOrSbc}
            isDealerSsoSbbOrSbcFlow={isDealerSsoSbbOrSbcFlow}
            isDisplayPolicyStatusMessage={isDisplayPolicyStatusMessage}
            onOdometerChange={onOdometerChange}
            isAwaitingInitialBalloonSetup={isAwaitingInitialBalloonSetup}
            onInitialBalloonSetupComplete={handleInitialBalloonSetupComplete}
          />
        </FormContentVisibility>
      )}
    </ModalDialog>
  );
};
